import React, {
  ReactNode,
  createContext,
  useContext,
  useMemo,
  useRef,
} from 'react';

import {
  ComprobantesBySVFId,
  useGetComprobantesBySVFs,
} from '../hooks/Reintegro/Comprobante/useComprobante';
import { useGetSVFs } from '../hooks/Reintegro/useSVFs';
import { SVF } from '../types/Siniestro/types';
import { useSiniestros } from './SiniestrosContext';

type SVFsContextType = {
  SVFs: SVF[] | undefined;
  mutateSVFs: () => Promise<SVF[] | undefined>;
  comprobantesBySVFs: ComprobantesBySVFId;
  mutateComprobantes: () => Promise<ComprobantesBySVFId | undefined>;
  mutateComprobantesByVetFormId: (vetFormId: number) => Promise<void>;
  vetFormId_Ref: React.MutableRefObject<number | null>;
  isValidating: boolean;
};

const SVFsContext = createContext<SVFsContextType | undefined>(undefined);

interface ProviderProps {
  children: ReactNode;
}

/**
 * Context que maneja SVFs y comprobantes del siniestro actual.
 *
 * Este context solo cambia cuando:
 * - Los SVFs del siniestro actual se actualizan (fetch, mutate)
 * - Los comprobantes de algún SVF se actualizan (fetch, mutate)
 * - El estado de validación cambia
 *
 * Consume currentSiniestro de SiniestrosContext.
 *
 * DECISIÓN DE DISEÑO: ¿Por qué SVFs y Comprobantes van juntos y no separados?
 *
 * 1. Alta cohesión funcional:
 *    - Los comprobantes pertenecen a un SVF específico (relación padre-hijo)
 *    - useGetComprobantesBySVFs(SVFs) requiere SVFs como input obligatorio
 *    - No puedes obtener comprobantes sin primero tener SVFs
 *
 * 2. Cambios acoplados:
 *    - Crear SVF → también inicializa estructura de comprobantes
 *    - Agregar comprobante → muta comprobantesBySVFs y puede actualizar SVF
 *    - Casi siempre cambian juntos en el flujo real de la app
 *
 * 3. Overhead innecesario de split:
 *    - Separar agregaría +2 contexts, +1 nivel nesting, más complejidad
 *    - Beneficio real mínimo: solo VeterinarioForm evitaría re-renders de comprobantes (manejado via React.memo)
 *    - Pero VeterinarioForm es modal (se monta/desmonta), no está siempre en pantalla
 *
 * 4. 7 dependencias es razonable:
 *    - El problema original era 14 deps en context monolítico (Siniestros)
 *    - 7 deps está dentro de límites aceptables
 *    - Split tiene sentido cuando >10 deps Y datos realmente independientes
 *
 * CONCLUSIÓN: Mantener juntos maximiza cohesión y minimiza complejidad sin
 * sacrificar performance. Split contexts para datos independientes, no para
 * datos naturalmente acoplados.
 */
export const SVFsContextProvider: React.FC<ProviderProps> = ({ children }) => {
  const { currentSiniestro } = useSiniestros();
  const vetFormId_Ref = useRef<number | null>(null);

  // No fetch SVFs para siniestros de tipo vacuna
  const isVacuna = currentSiniestro?.tipo === 'vacuna';
  const siniestroIdForSVFs = isVacuna ? undefined : currentSiniestro?.id;

  const { SVFs, isValidating: isSVFsValidating, mutate: mutateSVFs } = useGetSVFs(siniestroIdForSVFs);

  const {
    comprobantesBySVFs,
    isValidating: isComprobantesValidating,
    mutate: mutateComprobantes,
    mutateByVetFormId: mutateComprobantesByVetFormId,
  } = useGetComprobantesBySVFs(SVFs);

  const contextValue = useMemo(
    () => ({
      SVFs,
      mutateSVFs,
      comprobantesBySVFs,
      mutateComprobantes,
      mutateComprobantesByVetFormId,
      vetFormId_Ref,
      isValidating: isSVFsValidating || isComprobantesValidating,
    }),
    [
      SVFs,
      mutateSVFs,
      comprobantesBySVFs,
      mutateComprobantes,
      mutateComprobantesByVetFormId,
      isSVFsValidating,
      isComprobantesValidating,
    ],
  );

  return (
    <SVFsContext.Provider value={contextValue}>
      {children}
    </SVFsContext.Provider>
  );
};

export const useSVFs = (): SVFsContextType => {
  const context = useContext(SVFsContext);
  if (!context) {
    throw new Error('useSVFs must be used within a SVFsContextProvider');
  }
  return context;
};
